import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, Signal, WritableSignal, TemplateRef, computed, signal, viewChild } from "@angular/core";
import { RouterLink } from "@angular/router";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzFormInputType, InputChangeEvent, InputConfig } from "@ez-library/types/ez-form.types";
import { ColDef, GridOptions, ICellRendererParams } from "ag-grid-community";
import { TemplateRendererComponent } from "@app/shared/components/tables/ag-grid-cells/template-renderer/template-renderer.component";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { HEARING_PARTICIPATION_TERMINOLOGY } from "./hearing-participation.terminology";
import { AtHearingFormValues } from "../hearing-record.models";

export interface HearingAttendee {
  key: string;
  name: string;
  role: string;
  source: "panel" | "participant";
  isPresiding?: boolean;
}
export interface HearingAttendanceChange { key: string; present: boolean | null; }
interface AttendanceRow extends HearingAttendee { attendanceConfig: InputConfig[]; }

/** Scheduled roster only. Notice intent and representation never imply actual presence. */
@Component({
  selector: "phms-hearing-participants-section",
  standalone: true,
  imports: [CommonModule, RouterLink, UiLibraryModule, TerminologyIdentityDirective],
  templateUrl: "./hearing-participants-section.component.html",
  styleUrl: "./hearing-participants-section.component.scss",
})
export class HearingParticipantsSectionComponent {
  readonly rows = signal<AttendanceRow[]>([]);
  @Input() rosterLoading = false;
  @Input() rosterMessage = "";
  @Input() set attendees(value: readonly HearingAttendee[]) {
    this.rows.set(value.map(attendee => ({ ...attendee, attendanceConfig: [{
      name: "attendance", type: EzFormInputType.SELECT, label: `Actual attendance for ${attendee.name}`,
      hideLabel: true, width: "fill", row: 1,
      options: [{ value: "unrecorded", viewValue: "Not recorded" }, { value: "present", viewValue: "Present" }, { value: "absent", viewValue: "Absent" }],
    }] })));
  }
  @Input() presence!: Signal<Record<string, boolean>>;
  @Input() values!: WritableSignal<AtHearingFormValues>;
  @Input() canEdit!: Signal<boolean>;
  @Input() inputConfig: InputConfig[] = [];
  @Input() proceedingId: number | null = null;
  @Input() canReadContacts = false;
  @Output() attendanceChange = new EventEmitter<HearingAttendanceChange>();
  @Output() inputChange = new EventEmitter<InputChangeEvent>();
  readonly terminologyUsage = HEARING_PARTICIPATION_TERMINOLOGY;
  readonly text = createTerminologyUsageSignals(this.terminologyUsage);
  private readonly roleCell = viewChild<TemplateRef<unknown>>("roleCell");
  private readonly sourceCell = viewChild<TemplateRef<unknown>>("sourceCell");
  private readonly attendanceCell = viewChild<TemplateRef<unknown>>("attendanceCell");
  readonly columnDefaults: ColDef<AttendanceRow> = {
    flex: 1, minWidth: 160, editable: false, sortable: false, filter: false, resizable: true,
    wrapText: true, autoHeight: true, wrapHeaderText: true, autoHeaderHeight: true,
    suppressKeyboardEvent: ({ event }) => event.key === "Tab",
    cellStyle: { whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: "1.4", fontWeight: "normal", paddingTop: "6px", paddingBottom: "6px" },
  };
  readonly gridOptions: GridOptions<AttendanceRow> = {
    pagination: false, defaultColDef: this.columnDefaults, getRowId: ({ data }) => data.key,
    suppressCellFocus: true, suppressColumnVirtualisation: true,
  };
  readonly columns = computed<ColDef<AttendanceRow>[]>(() => [
    { field: "name", headerName: "Participant", flex: 1.4, minWidth: 180 },
    { field: "role", headerName: "Role", cellRenderer: TemplateRendererComponent, cellRendererParams: { ngTemplate: this.roleCell() } },
    { colId: "source", headerName: "Scheduled group", cellRenderer: TemplateRendererComponent, cellRendererParams: { ngTemplate: this.sourceCell() } },
    { colId: "attendance", headerName: "Actual attendance", minWidth: 230, cellRenderer: TemplateRendererComponent, cellRendererParams: { ngTemplate: this.attendanceCell() } },
  ]);

  attendanceCode(key: string): string {
    const value = this.presence()[key];
    return value === true ? "present" : value === false ? "absent" : "unrecorded";
  }
  attendanceLabel(key: string): string {
    return this.attendanceCode(key) === "present" ? "Present" : this.attendanceCode(key) === "absent" ? "Absent" : "Not recorded";
  }
  unrecordedCount(): number { return this.rows().filter(row => this.attendanceCode(row.key) === "unrecorded").length; }
  setAttendance(key: string, event: InputChangeEvent): void {
    if (!this.canEdit() || this.rosterLoading || !this.rows().some(row => row.key === key) || event.name !== "attendance"
      || !["present", "absent", "unrecorded"].includes(String(event.value))) return;
    this.attendanceChange.emit({ key, present: event.value === "unrecorded" ? null : event.value === "present" });
  }
  onInputChange(event: InputChangeEvent): void {
    if (!this.canEdit() || !this.inputConfig.some(field => field.name === event.name)) return;
    this.values.update(values => ({ ...values, [event.name]: event.value ?? "" }));
    this.inputChange.emit(event);
  }
  revealColumn(event: FocusEvent, params: ICellRendererParams): void {
    if (event.target instanceof HTMLElement && event.target.matches(":focus-visible")) params.api.ensureColumnVisible(params.column!);
  }
}
